import { useState } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import useEditFormStore from "../../../../../../state-management/useEditFormStore";
import {
	useDeleteCompanyTaxRate,
	useGetCompanyTaxRate,
} from "../../../../../../services/company-tax-rate.service";
import ActionPopover from "../../../../../../components/popovers/actionPopover";
import DeleteModal from "../../../../../../components/modals/DeleteModal";
import {
	useManageTableWithSearchParams,
	useManageTableWithSearchParamsData,
} from "../../../../../../helpers/useManageTable";
import {
	TaxRateDto,
	INCOME_PERIOD,
} from "../../../../../../models/tax-rate.dto";
import TableFilter from "../../../../../../components/table/filter/TableFilter";
import { rateTaxRuleColumns } from "../../../../../admin/system_configurations/views/tax_rules/tanstack-columns";
import TanStackEmployeeTable from "../../../../../../components/table/TanStackEmployeeTable";
import { TableId } from "../../../../../../models/table-configuration.dto";
import CompanyRateTaxRuleModalForm from "../modals/RateTaxRuleModalForm";

interface Props {
	companyId: number;
}

const CompanyRateTaxRuleView = ({ companyId }: Props) => {
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openModal, setOpenModal] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [isEditForm, setIsEditForm] = useState(false);

	const { tableAPIRef, tableData, isLoading, pageCount } =
		useManageTableWithSearchParams<TaxRateDto>({
			fetchHook: (params) => useGetCompanyTaxRate(companyId, params),
		});

	const handleClose = () => setAnchorEl(null);
	const handleOpenModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenModal(true);
	};

	const { formDataValues, setFormData } = useEditFormStore();
	const [deleting, setDeleting] = useState(false);
	const { mutate: deleteTaxRate } = useDeleteCompanyTaxRate();
	const { reloadTableData } = useManageTableWithSearchParamsData();

	const handleDeleteMethod = () => {
		setDeleting(true);
		deleteTaxRate(
			{ companyId, id: formDataValues?.id },
			{
				onSuccess: () => {
					reloadTableData();
					setOpenDeleteModal(false);
				},
				onSettled: () => setDeleting(false),
			},
		);
	};

	return (
		<>
			<div className="flex justify-between items-center mt-7 mb-8">
				<TableFilter
					fields={[
						{
							title: "",
							fieldName: "",
							fieldType: "SORT",
							options: [
								{ title: "Date Created", fieldName: "createdAt" },
								{ title: "Date Modified", fieldName: "modifiedAt" },
							],
						},
						{
							title: "Income Period",
							fieldName: "incomePeriod",
							fieldType: "CHECKBOX_LIST",
							options: INCOME_PERIOD,
						},
						{
							title: "On Direct Income",
							fieldName: "onDirectIncome",
							fieldType: "YES_NO",
						},
					]}
				/>
				<div
					className="flex items-center text-primary-blue text-sm cursor-pointer"
					onClick={handleOpenModal}
				>
					<AiOutlinePlus className="mr-1 text-sm" />
					Add New
				</div>
			</div>
			<TanStackEmployeeTable
				tableId={TableId.RATE_TAX_RULE}
				data={tableData}
				columns={rateTaxRuleColumns}
				loading={isLoading}
				onRowClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				onActionClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				initialColumnVisibility={{}}
				pageSize={10}
				pageCount={pageCount}
				apiRef={tableAPIRef}
			/>
			<ActionPopover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				handleClose={handleClose}
				setOpenDeleteModal={setOpenDeleteModal}
				setOpenEdit={setIsEditForm}
				setOpenModal={setOpenModal}
			/>
			<DeleteModal
				openDeleteModal={openDeleteModal}
				setOpenDeleteModal={setOpenDeleteModal}
				handleDeleteMethod={handleDeleteMethod}
				deleting={deleting}
			/>
			{openModal && (
				<CompanyRateTaxRuleModalForm
					open={openModal}
					setOpen={setOpenModal}
					title={isEditForm ? "Update Rate Rule" : "Add New Rate Rule"}
					isEditForm={isEditForm}
					tableAPIRef={tableAPIRef}
					companyId={companyId}
					reloadTableData={reloadTableData}
				/>
			)}
		</>
	);
};

export default CompanyRateTaxRuleView;
